import { useAtomValue } from "jotai";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";
import useCloudPermissions from "../../../hooks/useCloudPermissions";
import useWorkspacePermissions from "../../../hooks/useWorkspacePermissions";
import usePagePermissions from "../../../hooks/usePagePermissions";
import PageAPI from "../../api/PageAPI";
import {
  callFunctionIfExists,
  getErrorMessage,
} from "../../../utils/GeneralUtils";
import { workspaceIdAtom } from "../../../atoms/WorkspaceAtoms";

const useUpdateView = (pageID: string) => {
  const workspaceID = useAtomValue(workspaceIdAtom);
  const tenantID = useAtomValue(tenantIDAtom);
  const queryClient = useQueryClient();
  const { cPermissions } = useCloudPermissions();
  const { wPermissions } = useWorkspacePermissions();
  const { pPermissions } = usePagePermissions(pageID);

  const updateView = useMutation({
    mutationFn: ({
      parentViewID,
      viewID,
      payload,
      userActivity,
    }: {
      parentViewID?: string;
      viewID: string;
      payload: any;
      userActivity?: any;
      onSuccess?: () => void;
      onSettled?: () => void;
    }) => {
      if (
        !pPermissions.page_modify_view &&
        !wPermissions.workspace_modify_page &&
        !cPermissions.cloud_modify_page
      ) {
        return Promise.reject("You do not have permission to modify a view.");
      }

      if (parentViewID) {
        return PageAPI.updateSubView(
          workspaceID,
          pageID,
          viewID,
          payload,
          userActivity,
          parentViewID
        ).then((data) => {
          const items = data.data.data;
          return {
            page: null,
            views: [items.parentView, items.subView],
            quickCreates: null,
          };
        });
      }

      return PageAPI.updateView(
        workspaceID,
        pageID,
        viewID,
        payload,
        userActivity
      ).then((data) => {
        const { page, view, quickCreates } = data.data.data;
        return { page, views: [view], subView: null, quickCreates };
      });
    },
    onSuccess: ({ page, views, quickCreates }, { onSuccess }) => {
      page && queryClient.setQueryData(["page", pageID], page);
      quickCreates &&
        queryClient.setQueryData(["QuickCreates", tenantID], quickCreates);

      for (const view of views) {
        view && queryClient.setQueryData(["view", view?._id], view);
      }

      if (quickCreates) {
      }

      callFunctionIfExists(onSuccess);
    },
    onSettled: (_, __, { onSettled }) => {
      callFunctionIfExists(onSettled);
    },
  });

  return {
    updateView: updateView.mutate,
    viewUpdating: updateView.isPending,
    updateViewError: updateView.error
      ? getErrorMessage(updateView.error)
      : null,
  };
};

export default useUpdateView;
